import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ArrowRight, Plus } from "lucide-react";
import { SERVICE_CATEGORIES, PRICING } from "../data/site";

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [hash]);

  return (
    <div className="pt-24 lg:pt-32" data-testid="services-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">Our Services</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-brand-emerald tracking-tight">Professional Cleaning. <span className="text-brand-lime">Properly Done.</span></h1>
        <p className="mt-5 text-brand-muted text-base sm:text-lg max-w-2xl leading-relaxed">
          Six specialist services, twenty add-ons, one standard: spotless. Every service below is available across all of Lagos — Island and Mainland.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-8">
        {SERVICE_CATEGORIES.map((s, i) => (
          <article
            key={s.slug}
            id={s.slug}
            data-testid={`service-detail-${s.slug}`}
            className={`scroll-mt-28 bg-white rounded-3xl border border-[#D6E4DC] overflow-hidden grid lg:grid-cols-2 shadow-sm hover:shadow-xl hover:shadow-brand-emerald/5 transition-shadow ${i % 2 ? "" : ""}`}
          >
            <div className={`h-64 lg:h-auto ${i % 2 ? "lg:order-2" : ""}`}>
              <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center">
              <p className="text-xs font-bold text-brand-gold italic mb-2">{s.tagline}</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-emerald">{s.name}</h2>
              <p className="mt-4 text-brand-muted leading-relaxed">{s.description}</p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
                {s.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-brand-ink">
                    <Check className="w-4 h-4 text-brand-lime shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/booking?service=${encodeURIComponent(s.name)}`} data-testid={`service-book-${s.slug}`} className="px-6 py-3 rounded-full bg-brand-emerald text-white text-sm font-bold hover:bg-brand-lime hover:text-brand-emerald-dark transition-colors flex items-center gap-2">
                  Book This Service <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="px-6 py-3 rounded-full border-2 border-brand-emerald/20 text-brand-emerald text-sm font-bold hover:border-brand-gold hover:text-brand-gold transition-colors">
                  See Prices
                </Link>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="bg-brand-mint py-16" data-testid="addons-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">Extra Care for Every Space</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-emerald tracking-tight">Add-On Services</h2>
              <p className="mt-3 text-brand-muted max-w-xl">From deep cleaning to those special areas, our add-ons help you get the complete cleaning you need.</p>
            </div>
            <Link to="/pricing#addons" data-testid="addons-full-list-link" className="font-bold text-brand-emerald hover:text-brand-lime flex items-center gap-2 transition-colors w-fit">Full add-on price list <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {PRICING.addons.slice(0, 8).map((a) => (
              <div key={a.name} className="bg-white rounded-2xl p-5 border border-[#D6E4DC] hover:border-brand-lime/50 hover:shadow-md transition-all" data-testid={`addon-${a.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Plus className="w-4 h-4 text-brand-gold mb-2" />
                <p className="font-semibold text-sm text-brand-ink leading-snug">{a.name}</p>
                <p className="text-xs text-brand-lime font-bold mt-1.5">{a.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
